/**
 * Product media, with the two flags that decide what a customer actually receives:
 * `allowed_send` (may Maya attach this at all) and `featured` (which one leads).
 *
 * The image fallback is deliberate and visible. In a production build there is no /mock-media
 * route, so the tile says so in words instead of showing a broken-image icon — a grey box that
 * pretends to be a photo is worse than a label that admits what it is.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import type { Media } from '../lib/types.ts';
import { mediaUrl } from '../lib/media.ts';
import { Pill } from './Pill.tsx';

export interface GalleryProps {
  media: Media[];
  productTitle: string;
  onToggle?: (mediaId: string, flags: { allowedSend?: boolean; featured?: boolean }) => void;
}

function Tile({
  media,
  productTitle,
  onToggle,
}: {
  media: Media;
  productTitle: string;
  onToggle?: GalleryProps['onToggle'];
}): ReactNode {
  const [broken, setBroken] = useState(false);
  return (
    <figure
      className={clsx(
        'h1-card overflow-hidden',
        media.featured && 'ring-2 ring-[var(--h1-brand)]',
        !media.allowedSend && 'opacity-70',
      )}
      data-testid="gallery-tile"
      // The media id, so a test (and a person reading the DOM) can name one tile out of ten
      // without depending on the order the API happened to return them in — which changes the
      // moment a different photo is made to lead.
      data-media={media.id}
    >
      <div className="relative aspect-4/3 bg-surface-2">
        {broken ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-2 text-center">
            <span className="text-[11px] font-medium text-fg-muted">Image not served in this build</span>
            <span className="font-mono text-[10px] break-all text-fg-subtle">{media.path}</span>
          </div>
        ) : (
          <img
            src={mediaUrl(media)}
            alt={`${productTitle} — ${media.role}`}
            loading="lazy"
            onError={() => setBroken(true)}
            className="h-full w-full object-cover"
          />
        )}
        <div className="absolute top-1.5 left-1.5 flex gap-1">
          <Pill tone={media.role === 'hero' ? 'brand' : 'neutral'}>{media.role}</Pill>
          {!media.allowedSend && <Pill tone="danger">not sendable</Pill>}
        </div>
      </div>
      {onToggle && (
        <figcaption className="flex items-center justify-between gap-2 border-t border-line px-2 py-1.5 text-[11px] whitespace-nowrap">
          <label className="flex items-center gap-1.5 text-fg-muted">
            <input
              type="checkbox"
              checked={media.allowedSend}
              onChange={(e) => onToggle(media.id, { allowedSend: e.target.checked })}
              className="accent-[var(--h1-brand)]"
            />
            Sendable
          </label>
          <button
            type="button"
            onClick={() => onToggle(media.id, { featured: true })}
            disabled={media.featured}
            className="rounded px-1.5 py-0.5 font-medium text-brand-ink hover:bg-brand-soft disabled:opacity-40"
          >
            {media.featured ? 'Leads' : 'Set lead'}
          </button>
        </figcaption>
      )}
    </figure>
  );
}

export function Gallery({ media, productTitle, onToggle }: GalleryProps): ReactNode {
  const ordered = media.slice().sort((a, b) => a.sort - b.sort);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {ordered.map((m) => (
        <Tile key={m.id} media={m} productTitle={productTitle} {...(onToggle ? { onToggle } : {})} />
      ))}
    </div>
  );
}
